import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import type { TExhibitorWithEntries } from '@schemas'
import { PageHeading } from '@/components/page-heading'
import { SelectShowNotice } from '@/components/current-show'
import { useCurrentShow } from '@/context/current-show'
import { useExhibitors } from '@/api/hooks'
import { useShowSlug } from '@/hooks/useShowSlug'
import { showAware } from '@/lib/paths'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'

function countActiveEntries(entries?: TExhibitorWithEntries['entries']) {
  if (!Array.isArray(entries)) return 0
  return entries.filter(entry => entry.status !== 'not_entered').length
}

function getEntrantClub(exhibitor: TExhibitorWithEntries) {
  const value = typeof exhibitor.showClubName === 'string' ? exhibitor.showClubName.trim() : ''
  return value.length > 0 ? value : '—'
}

export default function EntrantsPage() {
  const { currentShow } = useCurrentShow()
  const clubsEnabled = currentShow?.enableClubs !== false
  const navigate = useNavigate()
  const currentSlug = useShowSlug()

  const exhibitorsQuery = useExhibitors(
    { showId: currentShow?.id, limit: 500 },
    { enabled: Boolean(currentShow?.id) }
  )

  const sortedEntrants = useMemo(() => {
    const data = exhibitorsQuery.data ?? []
    if (data.length === 0) return []
    const collator = new Intl.Collator(undefined, { usage: 'sort', sensitivity: 'base' })
    return [...data].sort((a, b) => collator.compare(a.name, b.name))
  }, [exhibitorsQuery.data])

  const handleOpenEntrant = useCallback(
    (exhibitorId: string | undefined) => {
      if (!exhibitorId) return
      navigate(showAware(`/entrants/${exhibitorId}`, { currentSlug }))
    },
    [currentSlug, navigate]
  )

  if (!currentShow?.id) {
    return (
      <SelectShowNotice description="Choose a show to review its entrants and open detailed records." />
    )
  }

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <PageHeading runLevel={1}>Entrants</PageHeading>
        <p className="text-muted-foreground">
          Review entrants for {currentShow.name} with their active entry counts
          {clubsEnabled ? ' and club details' : ''}, and jump into the detailed entrant view for
          maintenance.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Entrants</CardTitle>
          <CardDescription>
            Shows up to 500 entrants for this show, including entry totals (excluding Not Entered)
            {clubsEnabled ? ' and their club or affiliation' : ''}.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {exhibitorsQuery.isLoading ? (
            <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
              Loading entrants…
            </div>
          ) : exhibitorsQuery.isError ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-6 text-center text-sm text-destructive">
              {exhibitorsQuery.error?.message ?? 'Unable to load entrants. Try again shortly.'}
            </div>
          ) : sortedEntrants.length === 0 ? (
            <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
              No entrants found for this show.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Entrant</TableHead>
                  <TableHead className="text-right">Entries</TableHead>
                  {clubsEnabled ? <TableHead>Club</TableHead> : null}
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedEntrants.map(exhibitor => (
                  <TableRow key={exhibitor.id ?? exhibitor.name}>
                    <TableCell className="font-medium">{exhibitor.name}</TableCell>
                    <TableCell className="text-right">
                      {countActiveEntries(exhibitor.entries)}
                    </TableCell>
                    {clubsEnabled ? <TableCell>{getEntrantClub(exhibitor)}</TableCell> : null}
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        type="button"
                        onClick={() => handleOpenEntrant(exhibitor.id)}
                      >
                        Open
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
